import { useEffect, useMemo } from "react";
import { useGLTF } from "@react-three/drei";
import { useContext } from "react";
import * as THREE from "three";

import StateContext from "../../Context/StateContext";
import { apiClient } from "../../api/client";
import { useQuery } from "@tanstack/react-query";
import { calculatePrize } from "../commonUtils.js/commonFunc";
function Chains() {
  const globalState = useContext(StateContext);

  const chainData = useQuery({
    queryKey: ["chainData"],
    queryFn: () => apiClient.get("/chains"),
  });

  const getChainData = async () => {
    if (chainData.data && chainData.data.data.length > 0) {
      // Function to determine size + sort order by weight
      const getSizeAndOrder = (weight) => {
        const w = parseFloat(weight);

        if (w <= 1.2) return { size: 25, order: 0 }; // Thin
        if (w <= 2.2) return { size: 30, order: 1 }; // Medium
        return { size: 40, order: 2 }; // Thick
      };

      // Apply size + order and sort
      const sortedChains = [...chainData.data.data]
        .map((chain) => {
          const { size, order } = getSizeAndOrder(chain.weight);
          return {
            ...chain,
            size,
            order,
          };
        })
        .sort((a, b) => a.order - b.order);

      // Store all sorted chains
      globalState.setChain(sortedChains);

      // Use the first (smallest weight) chain as default
      const firstChainData = sortedChains[0];
      const firstChainWeight = parseFloat(firstChainData.weight, 10);

      globalState.setSelectedChainData(firstChainData);
      globalState.setChainWeight(firstChainWeight);
      globalState.setChainCharmSide("None");

      // Calculate price
      if (firstChainWeight) {
        try {
          const chainPrice = await calculatePrize(firstChainWeight);
          globalState.setPrices((prevPrices) => ({
            ...prevPrices,
            chainPrice,
          }));
        } catch (error) {
          console.error("Error calculating chain price:", error);
        }
      }
    }
  };

  useEffect(() => {
    getChainData();
  }, [chainData.data]);

  useEffect(() => {
    let charmType = globalState.activeCharm.charm;
    let scale = globalState.activeCharm.size;
    if (!scale || !charmType) return;
    if (globalState.chainCharmSide === "None") {
      chain.traverse((obj) => {
        if (obj.isMesh) {
          obj.visible = true;
        }
      });
      copiedChain.traverse((obj) => {
        if (obj.isMesh) {
          obj.visible = true;
        }
      });
      globalState.setChainCharmDetails([]);
    }
    let match = scale.match(/[\d.]+/);
    let number = match ? parseFloat(match[0]) : null;
    if (!number) return;

    let output;

    if (number < 1) {
      // Mapping for small values: 0.03 → 0.5, 0.25 → 1
      const m1 = (1 - 0.5) / (0.25 - 0.03); // ≈ 2.273
      const b1 = 0.5 - m1 * 0.03; // ≈ 0.4318
      output = m1 * number + b1;
    } else {
      // Mapping for larger values: 2 → 0.5, 4 → 1
      const m2 = (1 - 0.5) / (4 - 2); // = 0.25
      const b2 = 0.5 - m2 * 2; // = 0
      output = m2 * number + b2;
    }

    if (globalState.chainCharmSide === "Left") {
      chain.traverse((obj) => {
        if (obj.name === `Link_${globalState.chainCharmIndex}`) {
          obj.visible = false;

          const bbox = new THREE.Box3().setFromObject(obj);
          const worldCenter = bbox.getCenter(new THREE.Vector3());
          const height = bbox.max.y - bbox.min.y;

          globalState.setChainCharmDetails([
            {
              pos: worldCenter,
              height,
              side: "left",
              charm: charmType,
              scale: output * 2,
            },
          ]);
        }
      });

      copiedChain.traverse((obj) => {
        if (obj.name === `Link_${globalState.chainCharmIndex}`) {
          obj.visible = true;
        }
      });
    }

    if (globalState.chainCharmSide === "Right") {
      copiedChain.traverse((obj) => {
        if (obj.name === `Link_${globalState.chainCharmIndex}`) {
          obj.visible = false;

          const bbox = new THREE.Box3().setFromObject(obj);
          const worldCenter = bbox.getCenter(new THREE.Vector3());
          const height = bbox.max.y - bbox.min.y;

          globalState.setChainCharmDetails([
            {
              pos: worldCenter,
              height,
              side: "right",
              charm: charmType,
              scale: output * 2,
            },
          ]);
        }
      });

      chain.traverse((obj) => {
        if (obj.name === `Link_${globalState.chainCharmIndex}`) {
          obj.visible = true;
        }
      });
    }

    if (globalState.chainCharmSide === "Both") {
      let charmDetails = [];

      chain.traverse((obj) => {
        if (obj.name === `Link_${globalState.chainCharmIndex}`) {
          obj.visible = false;

          const bbox = new THREE.Box3().setFromObject(obj);
          const worldCenter = bbox.getCenter(new THREE.Vector3());
          const height = bbox.max.y - bbox.min.y;

          charmDetails.push({
            pos: worldCenter,
            height,
            side: "left",
            charm: charmType,
            scale: output * 2,
          });
        }
      });

      copiedChain.traverse((obj) => {
        if (obj.name === `Link_${globalState.chainCharmIndex}`) {
          obj.visible = false;

          const bbox = new THREE.Box3().setFromObject(obj);
          const worldCenter = bbox.getCenter(new THREE.Vector3());
          const height = bbox.max.y - bbox.min.y;

          charmDetails.push({
            pos: worldCenter,
            height,
            side: "right",
            charm: charmType,
            scale: output * 2,
          });
        }
      });

      globalState.setChainCharmDetails(charmDetails);
    }
  }, [globalState.chainCharmSide, globalState.activeCharm]);

  useEffect(() => {
    if (globalState.chainCharmDetails.length > 0) {
      globalState.setChainCharmDetails([]);
      globalState.setChainCharmIndex(0);
      globalState.setChainCharmSide("None");
      // chain.getObjectByName(`Link_${globalState.chainCharmIndex}`)?.visible = true;
      chain.traverse((obj) => {
        if (obj.name === `Link_${globalState.chainCharmIndex}`) {
          obj.visible = true;
        }
      });
      copiedChain.traverse((obj) => {
        if (obj.name === `Link_${globalState.chainCharmIndex}`) {
          obj.visible = true;
        }
      });
    }
  }, [globalState.text, globalState.currentRawPlateSize, globalState.fontNo]);

  const { scene: chain } = useGLTF(
    globalState.selectedChainData?.model
      ? `${process.env.REACT_APP_FILE_BASE_URL}${globalState.selectedChainData.model}`
      : `${process.env.PUBLIC_URL}/assets/glbs/dummy.glb`
  );

  function setSize() {
    globalState.globalMaterial.default.color.set(globalState.color);
    globalState.globalMaterial.default.roughness = globalState.globalRoughness;
    globalState.globalMaterial.default.metalness = globalState.globalMetalness;

    let totalLinks = 0;
    chain.traverse((obj) => {
      if (obj.name === "Socket") {
        obj.scale.z = globalState.fontThickness === 1.02 ? 6.6 : 7.2;
        obj.material = globalState.globalMaterial.default;
      }
      if (obj.isMesh && obj.name.includes("Link_")) {
        obj.receiveShadow = true;
        obj.castShadow = true;
        obj.material = globalState.globalMaterial.default;

        totalLinks += 1;
      }
    });

    if (totalLinks) {
      let index = Math.ceil(totalLinks / 4);
      if (index % 2 === 0) {
        index += 1;
      }
      globalState.setChainCharmIndex(index);
    }
  }

  const copiedChain = useMemo(() => {
    setSize();
    return chain.clone();
  }, [
    globalState.globalMaterial,
    globalState.pointArray,
    globalState.fontThickness,
    globalState.color,
    globalState.selectedChainData,
  ]);

  useEffect(() => {
    setSize();
  }, [
    globalState.globalMaterial,
    globalState.pointArray,
    globalState.fontThickness,
    globalState.color,
    globalState.selectedChainData,
  ]);

  useEffect(() => {
    if (globalState.selectedChainData) {
      chain.traverse((obj) => {
        if (obj.name.includes("Link")) {
          obj.visible = true;
        }
      });
      copiedChain.traverse((obj) => {
        if (obj.name.includes("Link")) {
          obj.visible = true;
        }
      });
    }
  }, [globalState.selectedChainData]);

  return (
    <>
      <group position={[0, 0, -globalState.fontThickness / 2]}>
        <primitive
          object={chain}
          rotation={[0, 0, 0.5]}
          position={globalState.pointArray[0]}
        />
      </group>
      <group position={[0, 0, -globalState.fontThickness / 2]}>
        <primitive
          object={copiedChain}
          rotation={[0, 0, -0.5]}
          position={globalState.pointArray[1]}
        />
      </group>
    </>
  );
}
export default Chains;
